import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { sendOtp, verifyOtp } from '../services/authService';
import { ERROR_MESSAGE, SUCCESS_MESSAGES } from '../constants/messages';
import './LoginModal.css';

const COUNTRY_CODE = '+268';
const COUNTRY_CODE_DIGITS = '268';
const PHONE_LENGTH = 8;
const OTP_LENGTH = 4;

type LoginStep = 'phone' | 'otp';

const LoginModal: React.FC = () => {
  const navigate = useNavigate();
  const { isModalOpen, pendingGame, closeLogin, setVerifiedPhone, setOtpSession } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [step, setStep] = useState<LoginStep>('phone');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [hasPhoneError, setHasPhoneError] = useState(false);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!isModalOpen) {
      setStep('phone');
      setPhoneDigits('');
      setOtp(Array(OTP_LENGTH).fill(''));
      setHasPhoneError(false);
      setIsSendingOtp(false);
      setIsVerifyingOtp(false);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (step === 'otp') {
      otpInputRefs.current[0]?.focus();
    }
  }, [step]);

  if (!isModalOpen) {
    return null;
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, PHONE_LENGTH);
    setPhoneDigits(value);
    if (hasPhoneError) {
      setHasPhoneError(false);
    }
  };

  const fullMsisdn = `${COUNTRY_CODE_DIGITS}${phoneDigits}`;
  const displayPhoneNumber = `${COUNTRY_CODE}${phoneDigits}`;

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (phoneDigits.length !== PHONE_LENGTH) {
      return;
    }

    setIsSendingOtp(true);
    setHasPhoneError(false);

    try {
      const response = await sendOtp(fullMsisdn);

      if (response.ok) {
        setOtpSession({
          msisdn: fullMsisdn,
          transactionId: response.transactionId,
          clientCorrelator: response.clientCorrelator,
          devOtp: response.devOtp,
        });
        setStep('otp');
        showSuccess(SUCCESS_MESSAGES.otpSent);
      } else {
        setHasPhoneError(true);
        showError();
      }
    } catch {
      setHasPhoneError(true);
      showError();
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);

    if (digit && index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;

    const newOtp = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((char, i) => {
      newOtp[i] = char;
    });
    setOtp(newOtp);
    otpInputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const otpValue = otp.join('');
    if (otpValue.length !== OTP_LENGTH) {
      return;
    }

    setIsVerifyingOtp(true);

    try {
      const response = await verifyOtp(fullMsisdn, otpValue);

      if (response.ok) {
        setVerifiedPhone(fullMsisdn);
        closeLogin();
        showSuccess(SUCCESS_MESSAGES.otpVerified);
        navigate('/subscribe');
      } else {
        showError(response.errorMessage || ERROR_MESSAGE);
      }
    } catch {
      showError();
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleBackToPhone = () => {
    setStep('phone');
    setOtp(Array(OTP_LENGTH).fill(''));
    setOtpSession(null);
  };

  const isPhoneValid = phoneDigits.length === PHONE_LENGTH;
  const isOtpValid = otp.every((digit) => digit !== '');

  return (
    <div className="login-modal-overlay" onClick={closeLogin}>
      <div className="login-modal" onClick={(e) => e.stopPropagation()}>
        <button className="login-modal-close" onClick={closeLogin} aria-label="Close">
          ×
        </button>

        {pendingGame && (
          <p className="login-modal-game-title">Play {pendingGame.title}</p>
        )}

        {step === 'phone' && (
          <div className="login-modal-content">
            <div className="login-modal-icon">📱</div>
            <h2 className="login-modal-title">Login to Play</h2>
            <p className="login-modal-subtitle">
              Enter your mobile number to continue
            </p>

            <form onSubmit={handlePhoneSubmit} className="login-form">
              <label className="login-label" htmlFor="phone-input">
                Mobile Number
              </label>
              <div className={`phone-input-group ${hasPhoneError ? 'phone-input-group--error' : ''}`}>
                <span className="phone-prefix">{COUNTRY_CODE}</span>
                <input
                  id="phone-input"
                  type="tel"
                  inputMode="numeric"
                  className="phone-input"
                  placeholder="Enter 8 digit number"
                  value={phoneDigits}
                  onChange={handlePhoneChange}
                  maxLength={PHONE_LENGTH}
                  autoFocus
                  disabled={isSendingOtp}
                />
              </div>
              <p className="phone-hint">
                {phoneDigits.length}/{PHONE_LENGTH} digits
              </p>

              <button
                type="submit"
                className="login-submit-button"
                disabled={!isPhoneValid || isSendingOtp}
              >
                {isSendingOtp ? 'Sending OTP...' : 'Continue'}
              </button>
            </form>
          </div>
        )}

        {step === 'otp' && (
          <div className="login-modal-content">
            <div className="login-modal-icon">🔐</div>
            <h2 className="login-modal-title">Verify OTP</h2>
            <p className="login-modal-subtitle">
              Enter the 4-digit code sent to{' '}
              <span className="phone-display">{displayPhoneNumber}</span>
            </p>

            <form onSubmit={handleOtpSubmit} className="login-form">
              <div className="otp-input-group" onPaste={handleOtpPaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      otpInputRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    className="otp-input"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    maxLength={1}
                    aria-label={`OTP digit ${index + 1}`}
                    disabled={isVerifyingOtp}
                  />
                ))}
              </div>

              <button
                type="submit"
                className="login-submit-button"
                disabled={!isOtpValid || isVerifyingOtp}
              >
                {isVerifyingOtp ? 'Verifying...' : 'Verify OTP'}
              </button>

              <button
                type="button"
                className="login-back-button"
                onClick={handleBackToPhone}
                disabled={isVerifyingOtp}
              >
                Back
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
